import { create } from 'zustand';
import { _ACTIVITY_ROOT, _NEW_LINE, _SPACE_CARD, _TEMPLATE_A, _freeCards, _libName, _pathSeparator, _rootName, charsMappingToURL } from '../constants';
import { useGlobalStore } from './globalZustand';
import { useTreeStore } from './treeZustand';
import { useUserStore } from './userZustand';
import { correctSentence } from '../../foo/cards';

interface URLVarsType {
    section: string | undefined;
    nodePath: string | undefined;
    prevNodePath: string | undefined;
    parentNodePath: string | undefined;
    nodePathBeforeLib: string | undefined;
    labels: string | undefined;
    templateCode: string | undefined;
    activityCode: string | undefined;
    clipboardFromPath: string | undefined;
    mainImageLabel: string | undefined;
}

interface getURLToType {
    talkFromNodePath: () => string;
    talkFromNextNodePath: (nextLabel:string) => string;
    talkFromCustomNodePath: (nodePath:string) => string;
    talkFromParentNodePath: () => string;
    talkFromPathBeforeLib: () => string;
    talkFromPrevNodePath: () => string;
    URLImport: (data:string) => string;
    bulkImport: () => string;
    help: () => string;
    lib: () => string;
    home: () => string;
    activities: () => string;
    drop: () => string;
    templateA: () => string;
    activity: (templateName:string, activityCode:string) => string;
    stories: () => string;
    homeworks: () => string;
    autoquiz: () => string;
    task: (taskNr:number, mainImageLabel:string, nodePath:string) => string;
    
}

interface URLState {
  URLVars: URLVarsType;
  setURLVars: (params: { [key: string]: string }) => void;
  getURLTo: () => getURLToType;
  isLibPath: () => boolean;
  isHomePage: () => boolean;
  isHelpPage: () => boolean;
  isTaskPage: () => boolean;
  isCardsPage: () => boolean;
  getNextNodePath: (nextLabel:string) => string;
  labels: string[];
  visibleLabels: string[];
  setClipboardFromPath: () => void;
  hasNestedCards: (label:string) => boolean;
  getParentNode: () => string | null;
  //REF-D
  addToPathMultipleChoices: (labels:string[]) => void;
  removeFromPathMultipleChoices: (labels:string[]) => void;
  checkIfCardIsInMultipleChoices: (label:string) => boolean;



}

export const useURLStore = create<URLState>()((set,get) => ({
    URLVars: {
        section: undefined,
        labels: undefined,
        nodePath: undefined,
        prevNodePath: undefined,
        parentNodePath: undefined,
        nodePathBeforeLib: undefined,
        templateCode: undefined,
        activityCode: undefined,
        clipboardFromPath: undefined,
        mainImageLabel: undefined
    },

    labels: [],
    visibleLabels: [],
    setClipboardFromPath: () => {
        set({
            URLVars: {
            ...get().URLVars,
            clipboardFromPath: get().URLVars.nodePath
            }
        });
    },
    //REF-D
    addToPathMultipleChoices: (labels:string[]) => {
        const { pathMultipleChoices, updateCardToggle } = useTreeStore.getState();
        // Filter out null labels and labels starting with _NEW_LINE
        labels = labels.filter(label => label !== null && !label.startsWith(_NEW_LINE));
        const existingLabels = pathMultipleChoices[get().URLVars.nodePath!] || [];
        const newLabels = [...new Set([...existingLabels, ...labels])];
        pathMultipleChoices[get().URLVars.nodePath!] = newLabels;
        useTreeStore.setState({ pathMultipleChoices: pathMultipleChoices, updateCardToggle: !updateCardToggle });
    },
    removeFromPathMultipleChoices: (labels:string[]) => {
        const { pathMultipleChoices, updateCardToggle } = useTreeStore.getState();
        // Filter out null labels and labels starting with _NEW_LINE
        labels = labels.filter(label => label !== null && !label.startsWith(_NEW_LINE));
        const existingLabels = pathMultipleChoices[get().URLVars.nodePath!] || [];
        const newLabels = existingLabels.filter(label => !labels.includes(label));
        pathMultipleChoices[get().URLVars.nodePath!] = newLabels;
        useTreeStore.setState({ pathMultipleChoices: pathMultipleChoices, updateCardToggle: !updateCardToggle });
    },
    checkIfCardIsInMultipleChoices: (label:string) => {
        const { pathMultipleChoices } = useTreeStore.getState();
        return pathMultipleChoices[get().URLVars.nodePath!]?.includes(label) || false;
    },
    
    isLibPath: () => {
        return get().URLVars.section === "talk" && get().URLVars.nodePath === _rootName+_pathSeparator+_libName;
    },
    isHomePage: () => {
        const { homeNodePath } = useGlobalStore.getState();
        return get().URLVars.section === "talk" && get().URLVars.nodePath === homeNodePath;
    },
    isHelpPage: () => {
        return get().URLVars.section === "talk" && get().URLVars.nodePath === "help";
    },
    isTaskPage: () => {
        return get().URLVars.section === "task";
    },
    isCardsPage: () => {
        return get().URLVars.section === "talk";
    },
    updateCardToggle: false,
 
    getNextNodePath: (nextLabel:string) => {
        let nodePath = get().URLVars.nodePath;
        return nodePath+_pathSeparator+charsMappingToURL(nextLabel);
    },
    hasNestedCards: (label:string) => {
        // Get the next node path
        let nextNodePath = get().getNextNodePath(label);
        // Get the labels of the next node path
        let labels = useTreeStore.getState().nodes[nextNodePath];
        return labels !== undefined && labels.length > 0;
    },
    getParentNode: () => {
        // If current path is root path, return null
        if(get().URLVars.nodePath === _rootName){
            return null;
        }

        // If there is less than 2 labels in path, return null
        let labels = get().URLVars.nodePath?.split(_pathSeparator) || [];
        if(labels.length < 2){
            return null;
        }

        // Return most recent label, which corresponds to parent node
        return labels[labels.length-1];

    },
    getURLTo: () => ({
        talkFromNodePath: () => {
            return "/page/talk/"+encodeURIComponent(get().URLVars.nodePath!);
        },
        talkFromPrevNodePath: () => {
            return "/page/talk/"+encodeURIComponent(get().URLVars.prevNodePath!);
        },
        talkFromParentNodePath: () => {
            return "/page/talk/"+encodeURIComponent(get().URLVars.parentNodePath!)
        },
        talkFromPathBeforeLib: () => {
            return "/page/talk/"+encodeURIComponent(get().URLVars.nodePathBeforeLib!);
        },
        talkFromNextNodePath: (nextLabel:string) => {
            return "/page/talk/"+encodeURIComponent(get().getNextNodePath(nextLabel));
        },
        talkFromCustomNodePath: (nodePath:string) => {
            return "/page/talk/"+encodeURIComponent(nodePath);
        },
        bulkImport: () => {
            //nodePath must not be processed! Only individual variables
            //must be processed, not already joined ones
            return "/page/bulkImport/"+encodeURIComponent(get().URLVars.nodePath!);
        },
        help: () => {
            return "/page/talk/help";
        },
        lib: () => {
            return "/page/talk/"+_rootName+_pathSeparator+_libName;
        },
        home: () => {
            const { homeNodePath } = useGlobalStore.getState();
            return "/page/talk/"+encodeURIComponent(homeNodePath);
        },
        activities: () => {
            return "/activities";
        },

        drop: () => {
            return "/drop/"+encodeURIComponent(get().URLVars.nodePath!);
        },
        templateA: () => {
            return _rootName+_pathSeparator+_libName+_pathSeparator+_ACTIVITY_ROOT+_pathSeparator+_TEMPLATE_A;
        },
        URLImport: (data:string) => {
            return window.location.protocol + "//" + window.location.host + "/import/" + encodeURIComponent(data);
        },
        activity: (templateName:string, activityCode:string) => {
            // templateName = "A","B",....
            return "/activity/"+templateName+"/"+encodeURI(activityCode);
        },
        stories: () => {
            return "/stories";
        },
        homeworks: () => {
            return "/homeworks";
        },
        autoquiz: () => {
            return "/autoquiz/"+encodeURIComponent(get().URLVars.nodePath!);
        },
        task: (taskNr:number, mainImageLabel:string, nodePath:string) => {
            return "/task/"+taskNr+"/"+encodeURIComponent(mainImageLabel)+"/"+encodeURIComponent(nodePath);
        }
        
    
            
    }),
    setURLVars: (params) => {
        // no charsMappingFromURL here!
        const processedParams = Object.fromEntries(
            Object.entries(params).map(([key, value]) => 
              [key, decodeURIComponent(value)] 
            ) 
        );
        if (processedParams.nodePath !== undefined){
            // set prevNodePath
            processedParams.prevNodePath = get().URLVars.nodePath || _rootName;

            // set nodePathBeforeLib
            if(!processedParams.prevNodePath.includes(_libName) &&
            processedParams.nodePath === _rootName+_pathSeparator+_libName){
                processedParams.nodePathBeforeLib = processedParams.prevNodePath;
            }
            
            // set parentNodePath
            if(processedParams.nodePath.includes(_pathSeparator)){
                let split = processedParams.nodePath.split(_pathSeparator);
                split.pop();
                processedParams.parentNodePath = split.join(_pathSeparator);
            }else{
                processedParams.parentNodePath = _rootName;
            }
        }

        

        let extra = {};
        if(processedParams.section === "talk"){
            // Update labels and visible labels
            const { l2Visibility, nodes, pathFlags } = useTreeStore.getState();
            const { isSubscribed } = useUserStore.getState();
            const { isParentMode } = useGlobalStore.getState();
            
            let labels: string[] = nodes[processedParams.nodePath!] || [];
            labels = correctSentence(labels);

            let visibleLabels = labels.filter(label => l2Visibility[label] !== undefined ? (l2Visibility[label] || isParentMode) : true);
            visibleLabels = visibleLabels.filter((label) => ![_SPACE_CARD, "", _NEW_LINE, null, undefined].includes(label.trim()));

            
            
            if (!isSubscribed === true) {
                visibleLabels = visibleLabels.slice(0, _freeCards) ;
            }

            extra = {
                labels: labels,
                visibleLabels: visibleLabels                
             };
        }else if(processedParams.section === undefined){
            processedParams.section = "";
        }
        

        set({
            URLVars: {
            ...get().URLVars,
            ...processedParams
            },
            ...extra
        });
  },

}));
